import type {
  BaseComponentConfig,
  Variables,
  Expression,
  JXGBoard,
  JXGElement,
} from '../types/interfaces';

// Import expression evaluator
import { evaluateExpression as evalExpr } from '../utils/expressionEvaluator';

/** Event data passed to handlers */
export interface DiagramEventData {
  type: string;
  detail: Record<string, any>;
  source: DiagramComponent;
  consumed: boolean;
  timestamp: number;
}

/**
 * Base class for all diagram components
 * Provides uniform interface for rendering, events, state, and composition
 */
export class DiagramComponent<TConfig extends BaseComponentConfig = BaseComponentConfig> {
  readonly id: string;
  readonly type: string;
  config: TConfig;
  readonly board: JXGBoard;

  parentState: Variables;
  localState: Variables;
  children: DiagramComponent[];
  elements: JXGElement[];
  jsxElements: JXGElement[]; // Alias for backward compatibility
  eventHandlers: Record<string, (event: DiagramEventData) => boolean>;
  debug: boolean;
  isDragging: boolean;
  onEvent?: (event: DiagramEventData) => void;

  constructor(config: TConfig, board: JXGBoard, parentState: Variables = {}) {
    this.config = config;
    this.board = board;
    this.id = config.id || this._generateId();
    this.type = config.type;
    this.parentState = parentState;
    this.localState = {};
    this.children = [];
    this.elements = [];
    this.jsxElements = this.elements; // Alias
    this.eventHandlers = {};
    this.debug = config.debug === true;
    this.isDragging = false;
  }

  /**
   * Render the component on the board
   * Override in subclasses to create JSXGraph elements
   */
  render(): JXGElement[] | JXGElement | void {
    throw new Error(`${this.type} must implement render()`);
  }

  /**
   * Update component when state changes
   */
  update(newState: Variables): void {
    // Skip update if component is currently being dragged
    if (this.isDragging) {
      if (this.debug) {
        console.log(`[${this.type}:${this.id}] Skipping update - currently being dragged`);
      }
      return;
    }

    if (this.debug) {
      console.log(`[${this.type}:${this.id}] Update called with:`, newState);
    }
    this.localState = { ...this.localState, ...newState };
    this._rerender();
  }

  /**
   * Clean up JSXGraph elements and event listeners
   */
  destroy(): void {
    // Remove all JSXGraph elements
    this.elements.forEach(el => {
      if (el && this.board.objects[el.id]) {
        this.board.removeObject(el);
      }
    });
    this.elements = [];
    this.jsxElements = this.elements;

    // Destroy children
    this.children.forEach(child => child.destroy());
    this.children = [];

    // Clear event handlers
    this.eventHandlers = {};
  }

  /**
   * Hit test - does point (x, y) intersect this component?
   */
  hitTest(x: number, y: number): boolean {
    return this.elements.some(el => {
      if (el.hasPoint) {
        return el.hasPoint(x, y);
      }
      return false;
    });
  }

  /**
   * Get component specificity for hit testing priority
   * Higher values = higher priority (more specific)
   */
  getSpecificity(): number {
    const defaults: Record<string, number> = {
      point: 100,
      text: 90,
      line: 80,
      circle: 50,
      polygon: 40,
      container: 10,
    };
    return defaults[this.type] || 50;
  }

  /**
   * Handle user interaction event
   */
  handleEvent(event: DiagramEventData): boolean {
    const handler = this.eventHandlers[event.type];
    if (handler) {
      return handler.call(this, event);
    }
    return false;
  }

  /**
   * Get current component state (merged parent + local)
   */
  getState(): Variables {
    return {
      ...this.parentState,
      ...this.localState,
    };
  }

  /**
   * Get value of a specific variable
   */
  getVariable(name: string): number | string | boolean | undefined {
    const state = this.getState();
    return state[name];
  }

  /**
   * Set variable value
   */
  setVariable(name: string, value: number | string | boolean): void {
    if (this.debug) {
      console.log(`[${this.type}:${this.id}] setVariable:`, name, '=', value);
    }
    this.localState[name] = value;
    this.update(this.localState);
    this._emitEvent('variable-change', { name, value });
  }

  /**
   * Add child component
   */
  addChild(component: DiagramComponent): void {
    this.children.push(component);
  }

  /**
   * Evaluate expression with current state
   */
  evaluateExpression(expression: Expression): number | string | boolean {
    if (typeof expression !== 'string') {
      return expression;
    }

    // Check if it's a template literal ${...}
    if (!expression.includes('${')) {
      return expression;
    }

    return evalExpr(expression, this.getState());
  }

  /**
   * Emit event (bubbles to parent)
   */
  protected _emitEvent(type: string, detail: Record<string, any>): void {
    if (this.onEvent) {
      this.onEvent({
        type,
        detail,
        source: this,
        consumed: false,
        timestamp: Date.now(),
      });
    }
  }

  /**
   * Re-render component (update JSXGraph elements)
   */
  protected _rerender(): void {
    if (this.debug) {
      console.log(`[${this.type}:${this.id}] _rerender called`);
    }
    this.destroy();
    this.render();
  }

  /**
   * Generate unique ID
   */
  private _generateId(): string {
    return `${this.type}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
